import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  HostBinding,
  Input,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatTooltip, TooltipComponent } from '@angular/material/tooltip';

import { Store } from '@ngxs/store';

import { PrefsState } from '@shared/states/prefs.state';
import { IncrementHelpItemStats, UpdateHelpCenterMode } from '@shared/modules/help-center/state/help-center.actions';
import {
  HelpCenterItemStatus,
  HelpCenterLanguage,
  HelpCenterMode,
  HelpItemSubject,
} from '@shared/modules/help-center/help-center.models';
import { OpenSidenav } from '@shared/states/dialog.actions';
import { HelpCenterSidenav } from '@shared/modules/help-center/components/help-sidenav/help-sidenav.component';

@Component({
  templateUrl: 'help-tooltip.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HelpTooltipComponent extends TooltipComponent {
  private store = inject(Store);

  @HostBinding('style.zoom')
  get zoom(): number | null {
    return this.isVisible() ? 1 : null;
  }

  @HostBinding('attr.aria-hidden')
  readonly ariaHidden = true;

  subject?: HelpItemSubject;

  status?: HelpCenterItemStatus;

  canEdit?: boolean;

  protected _onShow() {
    super._onShow();

    if (this.subject && this.store) {
      const lang = this.store.selectSnapshot(PrefsState.language) as HelpCenterLanguage;
      this.store.dispatch(new IncrementHelpItemStats(this.subject, 'hovers', lang));
    }
  }

  onShowHelpItem(): void {
    this.store.dispatch([
      new UpdateHelpCenterMode({
        mode: this.canEdit && this.status !== HelpCenterItemStatus.Complete ? HelpCenterMode.Edit : HelpCenterMode.Read,
        language: this.store.selectSnapshot(PrefsState.language) as HelpCenterLanguage,
      }),
      new OpenSidenav(HelpCenterSidenav, { data: { subject: this.subject } }),
    ]);
  }
}

@Directive({
  selector: '[helpTooltip]',
  exportAs: 'helpTooltip',
  standalone: false,
})
export class HelpTooltipDirective extends MatTooltip {
  @HostBinding('class.mat-tooltip-trigger')
  readonly trigger = true;

  @Input()
  set helpTooltip(message: string) {
    this.message = message;
  }

  @Input()
  subject?: HelpItemSubject;

  @Input()
  status?: HelpCenterItemStatus;

  @Input()
  canEdit?: boolean;

  constructor() {
    super();
    (this as any)._tooltipComponent = HelpTooltipComponent;
  }

  show(delay?: number): void {
    super.show(delay);

    if (this._tooltipInstance) {
      (this._tooltipInstance as HelpTooltipComponent).subject = this.subject;
      (this._tooltipInstance as HelpTooltipComponent).status = this.status;
      (this._tooltipInstance as HelpTooltipComponent).canEdit = this.canEdit;
    }
  }
}
